const ESCAPED_PIPE = '\u0000';

const convertMathDelimiters = (segment: string): string =>
  segment
    .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner: string) => `\n\n$$\n${inner.trim()}\n$$\n\n`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner: string) => `$$${inner.trim()}$$`);

const splitTableCells = (line: string): string[] => {
  const cells = line
    .trim()
    .replace(/\\\|/g, ESCAPED_PIPE)
    .split('|')
    .map((cell) => cell.split(ESCAPED_PIPE).join('\\|'));
  if (cells.length > 0 && cells[0].trim() === '') cells.shift();
  if (cells.length > 0 && cells[cells.length - 1].trim() === '') cells.pop();
  return cells;
};

const isTableSeparator = (line: string): boolean => {
  if (!line.includes('|') || !line.includes('-')) return false;
  const cells = splitTableCells(line);
  return cells.length > 0 && cells.every((c) => /^\s*:?-+:?\s*$/.test(c));
};

const repairTables = (segment: string): string => {
  if (!segment.includes('|') || !segment.includes('-')) return segment;
  const lines = segment.split('\n');
  const out = lines.slice();
  let i = 0;
  while (i + 1 < lines.length) {
    const header = lines[i];
    const sep = lines[i + 1];
    if (!header.includes('|') || isTableSeparator(header) || !isTableSeparator(sep)) {
      i += 1;
    } else {
      let end = i + 2;
      while (end < lines.length && lines[end].trim() !== '' && lines[end].includes('|')) end += 1;

      const start = i;
      const headerCells = splitTableCells(header);
      const sepCells = splitTableCells(sep);
      const dataRows = lines.slice(i + 2, end);
      const maxCols = Math.max(
        headerCells.length,
        ...dataRows.map((row) => splitTableCells(row).length),
      );
      const alreadyValid =
        headerCells.length === sepCells.length &&
        dataRows.every((row) => splitTableCells(row).length <= sepCells.length);

      if (!alreadyValid) {
        const padRow = (line: string): string => {
          const cells = splitTableCells(line).map((c) => c.trim());
          while (cells.length < maxCols) cells.push('');
          return `| ${cells.join(' | ')} |`;
        };
        const aligns = sepCells.map((c) => c.trim());
        const separator = Array.from({ length: maxCols }, (_v, k) =>
          aligns[k] && /^:?-+:?$/.test(aligns[k]) ? aligns[k] : '---',
        );
        out[start] = padRow(header);
        out[start + 1] = `| ${separator.join(' | ')} |`;
        dataRows.forEach((row, offset) => {
          out[start + 2 + offset] = padRow(row);
        });
      }
      i = end;
    }
  }
  return out.join('\n');
};

export const prepareAssistantMarkdown = (content: string): string => {
  if (!content) return content;
  return content
    .split(/(```[\s\S]*?```)/g)
    .map((segment, idx) => (idx % 2 === 1 ? segment : repairTables(convertMathDelimiters(segment))))
    .join('');
};
